// Shared IndexedDB helpers for Doctor Console consultation drafts (local + appointment list UI).

export const DRAFT_DB_NAME = "doctorConsoleDraftsDB";
export const DRAFT_STORE_NAME = "consultationDrafts";
export const DOCTOR_CONSOLE_DRAFT_KEY = "DoctorConsole";

const openDraftDB = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DRAFT_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DRAFT_STORE_NAME)) {
        db.createObjectStore(DRAFT_STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const getDraftRecord = async (key) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readonly");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

export const getDoctorConsoleDraftRecord = () =>
  getDraftRecord(DOCTOR_CONSOLE_DRAFT_KEY);

/** appointment_id on the record (supports legacy appointmentId field). */
export const getDraftAppointmentId = (record) =>
  record?.appointment_id ?? record?.appointmentId ?? null;

/** doctorId on the record (supports legacy doctor_id field). */
export const getDraftDoctorId = (record) =>
  record?.doctorId ?? record?.doctor_id ?? null;

/**
 * True when the draft belongs to doctorId.
 * Legacy records without doctorId are treated as not owned (so they get cleared).
 * If doctorId is missing, ownership cannot be enforced — returns true.
 */
export const isDraftOwnedByDoctor = (record, doctorId) => {
  if (doctorId == null || doctorId === "") return true;
  const draftDoctorId = getDraftDoctorId(record);
  if (draftDoctorId == null || draftDoctorId === "") return false;
  return String(draftDoctorId) === String(doctorId);
};

export const upsertDraftRecord = async (record) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.put(record);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const clearDraftRecord = async (key) => {
  const db = await openDraftDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DRAFT_STORE_NAME, "readwrite");
    const store = tx.objectStore(DRAFT_STORE_NAME);
    const request = store.put({
      key,
      data: null,
      patientId: null,
      patientName: null,
      doctorId: null,
      appointment_id: null,
      updatedAt: null,
      isDirty: null,
    });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

/** Clears Doctor Console draft when it belongs to another doctor (or legacy without doctorId). */
export const clearDoctorConsoleDraftIfNotOwnedBy = async (doctorId) => {
  const record = await getDoctorConsoleDraftRecord();
  if (!record?.data) return false;
  if (isDraftOwnedByDoctor(record, doctorId)) return false;
  await clearDraftRecord(DOCTOR_CONSOLE_DRAFT_KEY);
  return true;
};

const isSelectedSectionValueEmpty = (value) => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    if ("advice" in value) {
      const a = value.advice;
      return a == null || String(a).trim() === "";
    }
    if (Object.keys(value).length === 0) return true;
    return Object.values(value).every(
      (v) =>
        v == null ||
        v === "" ||
        (typeof v === "string" && v.trim() === "") ||
        (Array.isArray(v) && v.length === 0),
    );
  }
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const isSelectedSectionsDataAllEmpty = (data) => {
  if (!data || typeof data !== "object") return true;
  return Object.keys(data).every((k) => isSelectedSectionValueEmpty(data[k]));
};

const isMedicalBackgroundNewAllEmpty = (mb) => {
  if (!mb || typeof mb !== "object") return true;
  return Object.values(mb).every((section) => {
    if (!section || typeof section !== "object") return true;
    const n = section.new;
    if (n == null) return true;
    if (Array.isArray(n)) return n.length === 0;
    if (typeof n === "object") return Object.keys(n).length === 0;
    return false;
  });
};

const isVitalsAllEmpty = (vitals) => {
  if (!Array.isArray(vitals) || vitals.length === 0) return true;
  return vitals.every(
    (vital) =>
      vital &&
      typeof vital === "object" &&
      Object.prototype.hasOwnProperty.call(vital, "value") &&
      vital.value === "",
  );
};

export const isConsultationDraftContentEmpty = (
  selectedSectionsData,
  medicalBackground,
  vitals,
) =>
  isSelectedSectionsDataAllEmpty(selectedSectionsData) &&
  isMedicalBackgroundNewAllEmpty(medicalBackground) &&
  isVitalsAllEmpty(vitals);

/**
 * Non-empty local draft appointment_id for this doctor, or null.
 * When doctorId is provided, drafts for other doctors (or legacy without doctorId) are ignored.
 */
export const getLocalDoctorConsoleDraftAppointmentId = async (doctorId) => {
  const record = await getDoctorConsoleDraftRecord();
  if (!record?.data) return null;
  if (!isDraftOwnedByDoctor(record, doctorId)) return null;
  const { selectedSectionsData, medicalBackground, vitals } = record.data;
  if (
    isConsultationDraftContentEmpty(
      selectedSectionsData,
      medicalBackground,
      vitals,
    )
  ) {
    return null;
  }
  const appointmentId = getDraftAppointmentId(record);
  return appointmentId != null && appointmentId !== ""
    ? String(appointmentId)
    : null;
};

/** Whether IndexedDB has non-empty draft for this appointment_id (optionally scoped to doctorId). */
export const hasDoctorConsoleDraftForAppointment = async (
  appointmentId,
  doctorId,
) => {
  if (appointmentId == null || appointmentId === "") return false;
  const localAppointmentId =
    await getLocalDoctorConsoleDraftAppointmentId(doctorId);
  return (
    localAppointmentId != null &&
    String(localAppointmentId) === String(appointmentId)
  );
};

export const EMPTY_SELECTED_SECTIONS_DATA = {
  "Chief Complaints": [],
  Examinations: [],
  Diagnosis: [],
  "Adult Vaccination": [],
  Procedures: [],
  Medications: [],
  "Tests/Investigations": [],
  Referral: [],
  FollowUp: [],
  IPDAdmissionReferral: [],
  Attachments: [],
  InitialObservation: [],
  MedicalAdvice: {},
};

export const buildConsultationDraftData = (
  selectedSectionsData,
  medicalBackground,
  vitals,
) => ({
  selectedSectionsData,
  medicalBackground,
  vitals,
});
